import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { AccountCommandList } from "./AccountCommandTab";
import { forexApi, type FollowRelation } from "@/lib/forex-api";

const statusLabel: Record<string, { cls: string; label: string }> = {
  active: { cls: "bg-emerald-500/10 text-emerald-500", label: "跟单中" },
  paused: { cls: "bg-amber-500/10 text-amber-500", label: "已暂停" },
  stopped: { cls: "bg-red-500/10 text-red-500", label: "已停止" },
};

function formatTime(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function FollowDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const relationId = searchParams.get("id") || "";
  const [relation, setRelation] = useState<FollowRelation | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    forexApi.listFollowRelations()
      .then((relations) => {
        if (cancelled) return;
        const matched = relations.items.find((item) => item.relation_id === relationId) || null;
        setRelation(matched);
        if (!matched) setError("未找到跟单关系，请从跟单列表重新进入。");
      })
      .catch(() => {
        if (!cancelled) setError("跟单详情接口不可用，请稍后重试。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [relationId]);

  const handleAction = async (nextAction: "pause" | "resume" | "stop") => {
    if (!relation) return;
    setAction(nextAction);
    try {
      const updated = await forexApi.patchFollowRelation(relation.relation_id, {
        action: nextAction,
        reason: `${nextAction} from admin detail`,
      });
      setRelation(updated);
      showToast("success", nextAction === "pause" ? "跟单已暂停" : nextAction === "resume" ? "跟单已恢复" : "跟单已停止");
    } catch {
      showToast("error", "跟单操作失败，请确认后端服务和登录态");
    } finally {
      setAction("");
    }
  };

  const st = relation ? statusLabel[relation.status] || { cls: "bg-surface text-text-secondary", label: relation.status } : null;

  const tabs = [
    { key: "overview", label: "跟单概览" },
    { key: "commands", label: "账号命令与回报" },
  ];

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "跟单与执行", href: "/forex/follow" }, { label: "跟单详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">跟单详情</h2>
          <p className="text-xs text-text-muted mt-0.5">{relation ? `${relation.follower_name} · ${relation.relation_id} · ${relation.follower_account_id}` : relationId || "未选择跟单关系"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {relation?.status === "active" && <button onClick={() => handleAction("pause")} disabled={!!action} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-50 cursor-pointer whitespace-nowrap">{action === "pause" ? <><i className="ri-loader-4-line animate-spin text-sm" />暂停中</> : <><i className="ri-pause-line text-sm" />暂停跟单</>}</button>}
          {relation?.status === "paused" && <button onClick={() => handleAction("resume")} disabled={!!action} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 disabled:opacity-50 cursor-pointer whitespace-nowrap">{action === "resume" ? <><i className="ri-loader-4-line animate-spin text-sm" />恢复中</> : <><i className="ri-play-line text-sm" />恢复跟单</>}</button>}
          {relation && relation.status !== "stopped" && <button onClick={() => handleAction("stop")} disabled={!!action} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50 cursor-pointer whitespace-nowrap"><i className="ri-stop-line text-sm" />停止跟单</button>}
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回列表</button>
        </div>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && relation && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">状态</p><p className={`text-xs font-semibold mt-1 ${st?.cls}`}>{st?.label}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">累计盈亏</p><p className={`text-lg font-semibold font-mono ${relation.total_pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}>{relation.total_pnl >= 0 ? "+" : ""}${relation.total_pnl.toFixed(2)}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">成功率</p><p className="text-lg font-semibold font-mono text-emerald-500">{relation.success_rate}%</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">持仓数</p><p className="text-lg font-semibold font-mono text-text-primary">{relation.current_positions}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">最大回撤</p><p className="text-lg font-semibold font-mono text-amber-500">{relation.max_drawdown}%</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">最近命令</p><p className="text-sm font-semibold font-mono text-text-primary">{relation.last_command_id}</p></div>
          </div>

          <div className="flex items-center gap-1 flex-wrap border-b border-surface-border pb-0">
            {tabs.map((tb) => (
              <button key={tb.key} onClick={() => setActiveTab(tb.key)} className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap cursor-pointer ${activeTab === tb.key ? "bg-white text-brand border border-surface-border border-b-white -mb-px" : "text-text-secondary hover:text-text-primary"}`}>{tb.label}</button>
            ))}
          </div>

          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-surface-border p-4">
                <h3 className="text-sm font-semibold text-text-primary mb-3">跟单参数</h3>
                <div className="space-y-2">
                  {[
                    { label: "跟单关系ID", value: relation.relation_id, mono: true },
                    { label: "用户标识", value: relation.user_ref, mono: true },
                    { label: "子账号", value: relation.follower_account_masked, mono: true },
                    { label: "智能体", value: relation.agent_name },
                    { label: "经纪商", value: `${relation.broker} / ${relation.server}` },
                    { label: "跟单模式", value: relation.copy_mode },
                    { label: "跟单参数", value: String(relation.copy_value), mono: true },
                    { label: "开通时间", value: formatTime(relation.opened_at) },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between py-1.5 border-b border-surface-border last:border-0 gap-3">
                      <span className="text-xs text-text-muted">{row.label}</span>
                      <span className={`text-xs font-medium text-text-primary text-right ${row.mono ? "font-mono" : ""}`}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-surface-border p-4">
                <h3 className="text-sm font-semibold text-text-primary mb-3">关联对象</h3>
                <div className="space-y-2">
                  <Link to={`/forex/agents/detail?id=${relation.agent_id}`} className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-surface hover:bg-brand/10 cursor-pointer"><span className="text-xs text-text-secondary">智能体</span><span className="text-xs text-text-primary font-mono">{relation.agent_id}</span><i className="ri-arrow-right-line text-text-muted" /></Link>
                  <Link to={`/forex/sub-detail/detail?id=${relation.follower_account_id}`} className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-surface hover:bg-brand/10 cursor-pointer"><span className="text-xs text-text-secondary">子账号</span><span className="text-xs text-text-primary font-mono">{relation.follower_account_id}</span><i className="ri-arrow-right-line text-text-muted" /></Link>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {relation.symbols.map((symbol) => <span key={symbol} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono bg-surface text-text-secondary">{symbol}</span>)}
                </div>
              </div>
            </div>
          )}

          {activeTab === "commands" && (
            <div className="rounded-lg border border-surface-border p-4">
              <p className="mb-3 text-xs text-text-muted">该账号执行此智能体信号的命令及回报；未生成命令的准入原因可在共享信号详情查看。</p>
              <AccountCommandList key={relation.relation_id} accountScope={relation.follower_account_id} agentId={relation.agent_id} />
            </div>
          )}

        </>
      )}

      {toast && (<div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease-out]"><div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}><i className={`${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} text-base`} />{toast.msg}</div></div>)}
    </div>
  );
}
